import { SVGRenderer } from './SVGRenderer';
import { AnimationEngine } from './AnimationEngine';
import { Timeline } from './Timeline';
import { SpeechManager } from '../utils/SpeechManager';
import { EngineConfig, ConstructionStep, EngineEvent, EventCallbackData } from '../types';

import { Compass } from '../objects/Compass';
import { Ruler } from '../objects/Ruler';
import { ConstructionRegistry } from '../registry/ConstructionRegistry';
import { registerAllConstructions } from '../registry/registerAll';
import { JSONParser } from '../json/JSONParser';
import { HumanTextbookParser } from '../json/HumanTextbookParser';
import { JSONValidator } from '../json/JSONValidator';
import { TeacherJSON, ValidationResult } from '../json/JSONSchema';

export class GeometryEngine {
  public renderer: SVGRenderer;
  public animationEngine: AnimationEngine;
  public timeline: Timeline;

  public compass: Compass;
  public ruler: Ruler;

  constructor(containerOrSvg: HTMLElement | SVGSVGElement, config: EngineConfig = {}) {
    this.renderer = new SVGRenderer(containerOrSvg, config);
    this.animationEngine = new AnimationEngine(config.animationSpeed ?? 1.0);
    this.timeline = new Timeline(this.animationEngine);

    // Register Grade 1 to Grade 10 construction modules
    registerAllConstructions();

    // Instantiate primary tools
    this.compass = new Compass(this.renderer);
    this.ruler = new Ruler(this.renderer);

    this.compass.setVisible(false);
    this.ruler.setVisible(false);
  }

  /**
   * Load and play construction from Teacher JSON (Parametric or Action Steps)
   */
  public activeJustification: string | null = null;

  public loadTeacherJSON(jsonInput: string | TeacherJSON): ValidationResult {
    let parsedObj: any = jsonInput;

    if (typeof jsonInput === 'string') {
      try {
        parsedObj = JSON.parse(jsonInput);
      } catch (err: any) {
        return {
          valid: false,
          errors: [`SyntaxError in JSON: ${err.message}`],
          warnings: [],
        };
      }
    }

    const validation = JSONValidator.validate(parsedObj);
    if (!validation.valid) {
      return validation;
    }

    this.clear();

    // Check if step-based Action JSON (with step.action or step.actions)
    if (parsedObj.steps && Array.isArray(parsedObj.steps) && (parsedObj.steps[0]?.action || parsedObj.steps[0]?.actions)) {
      const { steps, justification } = HumanTextbookParser.parse(parsedObj, this);
      this.activeJustification = justification || null;
      this.timeline.setSteps(steps);
    } else {
      this.activeJustification = parsedObj.justification || null;
      const steps = JSONParser.parse(parsedObj as TeacherJSON, this);
      this.timeline.setSteps(steps);
    }

    return validation;
  }

  public validateTeacherJSON(jsonInput: string | TeacherJSON): ValidationResult {
    let parsedObj: any = jsonInput;
    if (typeof jsonInput === 'string') {
      try {
        parsedObj = JSON.parse(jsonInput);
      } catch (err: any) {
        return {
          valid: false,
          errors: [`SyntaxError in JSON: ${err.message}`],
          warnings: [],
        };
      }
    }
    return JSONValidator.validate(parsedObj);
  }

  /**
   * Dynamic helper to construct by name from registry
   */
  public constructByName(name: string, options: Record<string, any> = {}): ConstructionStep[] {
    const ctor = ConstructionRegistry.get(name);
    if (!ctor) {
      throw new Error(`Construction '${name}' is not registered.`);
    }
    this.clear();
    const builder = new ctor(this.renderer, this.compass, this.ruler);
    const steps = builder.buildSteps(options);
    this.timeline.setSteps(steps);
    return steps;
  }

  // --- Shortcut Methods ---

  public construct90DegreeAngle(options: { x?: number; y?: number; size?: number } = {}): ConstructionStep[] {
    return this.constructByName('90deg', options);
  }

  public constructPerpendicularBisector(options: { x?: number; y?: number; size?: number } = {}): ConstructionStep[] {
    return this.constructByName('perpendicular-bisector', options);
  }

  public constructAngleBisector(options: { x?: number; y?: number; size?: number } = {}): ConstructionStep[] {
    return this.constructByName('angle-bisector', options);
  }

  public constructEquilateralTriangle(options: { x?: number; y?: number; size?: number } = {}): ConstructionStep[] {
    return this.constructByName('equilateral', options);
  }

  public constructParallelLine(options: { x?: number; y?: number; size?: number } = {}): ConstructionStep[] {
    return this.constructByName('parallel', options);
  }

  public constructRegularPolygon(options: { x?: number; y?: number; radius?: number } = {}): ConstructionStep[] {
    return this.constructByName('hexagon', options);
  }

  // --- Playback API ---

  public play(): void {
    this.timeline.play();
  }

  public pause(): void {
    this.timeline.pause();
  }

  public restart(): void {
    this.timeline.restart();
  }

  public seek(timeMs: number): void {
    this.timeline.seek(timeMs);
  }

  public gotoStep(stepIndex: number): void {
    this.timeline.gotoStep(stepIndex);
  }

  public nextStep(): void {
    this.timeline.nextStep();
  }

  public prevStep(): void {
    this.timeline.prevStep();
  }

  public setSpeed(speed: number): void {
    this.animationEngine.setSpeed(speed);
    SpeechManager.getInstance().setSpeed(speed);
  }

  public on(event: EngineEvent, callback: (data: EventCallbackData) => void): () => void {
    return this.timeline.on(event, callback);
  }

  public clear(): void {
    this.timeline.pause();
    this.renderer.clearAll();
    this.compass = new Compass(this.renderer);
    this.ruler = new Ruler(this.renderer);
    this.compass.setVisible(false);
    this.ruler.setVisible(false);
  }

  // --- Export Methods ---

  public exportSVG(): string {
    const clone = this.renderer.svg.cloneNode(true) as SVGSVGElement;
    const toolLayer = clone.querySelector('#layer-tools');
    if (toolLayer) toolLayer.setAttribute('display', 'none');
    return new XMLSerializer().serializeToString(clone);
  }

  public exportPNG(filename: string = 'geometry-construction.png'): void {
    const svgData = this.exportSVG();
    const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 2000;
      canvas.height = 1400;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = this.renderer.config.backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        const a = document.createElement('a');
        a.download = filename;
        a.href = canvas.toDataURL('image/png');
        a.click();
      }
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }

  public exportPDFReadySVG(): string {
    const svgStr = this.exportSVG();
    return `<?xml version="1.0" encoding="UTF-8" standalone="no"?>\n` + svgStr;
  }
}
